import type { Marker, MarkerClusterGroup } from "leaflet";
import * as L from "leaflet";
import type { ParseResult } from "papaparse";
import { parse } from "papaparse";
import towerIconUrl from "./assets/towerIcon.svg";
import towerShadowUrl from "./assets/towerShadow.svg";
import "./style.scss";

const DATASOURCE_URL =
    "https://docs.google.com/spreadsheets/d/e/2PACX-1vT_zrH-JtdEQrTVlGuRkxyhEy89Qvp1BS9A3ADGXS2uAex1exA27b6ydJRiaFX-T4-b1GgEor-OOnaQ/pub?gid=0&single=true&output=csv";

const popupTemplate: string = `
<h3>%PAESE%</h3>
<h5>%CHIESA%</h5>
<ul>
    <li><b>N° campane: </b>%NUMERO%</li>
    <li><b>Peso: </b>%PESO% Kg</li>
    <li><b>Nota: </b>%NOTA%</li>
    <li><b>Condizione: </b>%SUONABILE%</li>
</ul>
`.replace(/(\n|\t|\s\s\s\s)/g, "");

const TOWER_WIDTH_PX = 24;

type LeafletGlobal = typeof globalThis & {
    L: typeof L;
};

interface RawTower {
    lat?: string;
    lng?: string;
    nota?: string;
    numero?: string;
    peso?: string;
    condizione?: string;
    luogo?: string;
    chiesa?: string;
}

interface Tower {
    lat: number;
    lng: number;
    id: number;
    nota: string;
    numero: string;
    peso: number | "Sconosciuto";
    condizione: string;
    paese: string;
    chiesa: string;
}

type LoadState = "loading" | "error" | "ready";

window.onload = () => {
    void main();
};

async function main() {
    (globalThis as LeafletGlobal).L = L;
    await import("leaflet.markercluster");

    const map = L.map("map", {
        maxZoom: 19,
        minZoom: 8,
        center: {
            lat: 45.45627757127799,
            lng: 11.30149841308594,
        },
        zoom: 10,
        inertia: true,
    });

    map.addLayer(
        L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
            maxZoom: 19,
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        }),
    );

    const cluster = L.markerClusterGroup({
        showCoverageOnHover: false,
        disableClusteringAtZoom: 17,
        spiderfyOnMaxZoom: false,
        singleMarkerMode: false,
        iconCreateFunction: (c) => {
            return L.divIcon({
                html: c.getChildCount().toString(),
                className: "clusterIcon",
                iconSize: [45, 45],
            });
        },
    });

    map.addLayer(cluster);

    const retryButton = document.querySelector<HTMLButtonElement>("#dataRetry");
    retryButton?.addEventListener("click", () => {
        loadTowers(cluster);
    });

    await loadTowers(cluster);
}

async function loadTowers(cluster: MarkerClusterGroup) {
    setLoadState("loading");
    cluster.clearLayers();

    try {
        const towers = await fetchTowers();
        addTowers(cluster, towers);
        setLoadState("ready");
    } catch (e) {
        console.error("Failed to load towers", e);
        setLoadState("error");
    }
}

async function fetchTowers(): Promise<Tower[]> {
    const response = await fetch(DATASOURCE_URL, { cache: "no-store" });

    if (!response.ok) {
        throw new Error("Datasource request failed");
    }

    const csv = await response.text();
    const result: ParseResult<RawTower> = parse<RawTower>(csv, {
        header: true,
        skipEmptyLines: true,
    });

    if (result.errors.length > 0) {
        throw new Error("Datasource parsing failed");
    }

    const towers = result.data.reduce<Tower[]>((accumulator, tower, index) => {
        console.log("Tower", index, tower);
        const lat = Number(tower.lat?.replace(",", "."));
        const lng = Number(tower.lng?.replace(",", "."));

        if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
            return accumulator;
        }

        accumulator.push({
            id: index,
            lat,
            lng,
            nota: normalizeTowerText(tower.nota),
            numero: normalizeTowerText(tower.numero),
            peso: parseTowerWeight(tower.peso),
            condizione: normalizeTowerText(tower.condizione),
            paese: normalizeTowerText(tower.luogo),
            chiesa: normalizeTowerText(tower.chiesa),
        });

        return accumulator;
    }, []);

    if (towers.length === 0) {
        throw new Error("Datasource has no valid towers");
    }

    return towers;
}

function normalizeTowerText(value: string | undefined) {
    return value?.trim() ?? "";
}

function parseTowerWeight(value: string | undefined): Tower["peso"] {
    const normalizedValue = normalizeTowerText(value);

    if (normalizedValue === "") {
        return "Sconosciuto";
    }

    const weight = Number(normalizedValue);

    return Number.isFinite(weight) ? weight : "Sconosciuto";
}

function addTowers(cluster: MarkerClusterGroup, towerList: Tower[]) {
    const icon = L.icon({
        iconUrl: towerIconUrl,
        iconSize: [TOWER_WIDTH_PX, TOWER_WIDTH_PX * 4],
        iconAnchor: [TOWER_WIDTH_PX / 2, TOWER_WIDTH_PX * 4],
        popupAnchor: [0, -TOWER_WIDTH_PX * 2],
        shadowUrl: towerShadowUrl,
        shadowSize: [TOWER_WIDTH_PX, TOWER_WIDTH_PX * 4],
        shadowAnchor: [TOWER_WIDTH_PX / 2 - 2, TOWER_WIDTH_PX * 4 - 2],
    });

    const markers: Marker[] = [];

    towerList.forEach((t) => {
        const marker = L.marker([t.lat, t.lng], {
            title: t.paese + ", " + t.chiesa,
            icon,
        });
        marker.bindPopup(customPopup(t), {
            className: "tower-popup",
            closeButton: false,
        });
        markers.push(marker);
    });

    cluster.addLayers(markers);
}

function setLoadState(state: LoadState) {
    const overlay = document.querySelector<HTMLElement>("#dataOverlay");
    const status = document.querySelector<HTMLElement>("#dataStatus");

    if (!overlay || !status) {
        return;
    }

    overlay.dataset.state = state;
    overlay.hidden = state === "ready";
    if (state === "error") {
        status.textContent = "Impossibile caricare i dati. Riprova tra qualche istante.";
    } else {
        status.textContent = "Caricamento dati...";
    }
}

function customPopup(tower: Tower) {
    return popupTemplate
        .replace("%PAESE%", tower.paese)
        .replace("%CHIESA%", tower.chiesa)
        .replace("%NUMERO%", tower.numero.toString())
        .replace("%NOTA%", tower.nota)
        .replace("%SUONABILE%", tower.condizione)
        .replace("%PESO%", tower.peso.toString());
}
